import {
  EVIDENCE_POWER_USER_BANNER_COUNT_THRESHOLD,
  evidencePowerUserBannerDismissedAtom,
  evidencePowerUserDevForceShowBannerAtom,
} from '@/atoms/evidenceTrialQueryCountAtom';
import { evidencePowerUserUpgradeModalOpenAtom } from '@/atoms/paywallAtoms';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';
import { useIsEvidencePowerUserEnabled } from '@/hooks/useIsEvidencePowerUserEnabled';
import useReverseTrial from '@/hooks/useReverseTrial';
import { useUserActivityActionCount } from '@/hooks/useUserActivityActionCount';
import { track } from '@/lib/analytics/track';
import { cn } from '@/lib/cn';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import {
  Banner,
  BannerAction,
  BannerClose,
  BannerContent,
  BannerDescription,
  BannerTitle,
} from '@heidi/design-system/banner';
import { Button } from '@heidi/design-system/button';
import { TierProFilled } from '@heidi/design-system/icons';
import { ButtonAction } from '@heidi/runtime/analytics-vocabulary';
import { UserActivityAction } from '@heidi/schema/ml-scribe';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Rocket } from 'lucide-react';
import { AnimatePresence } from 'motion/react';
import * as m from 'motion/react-m';
import { useCallback, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

interface EvidencePowerUserBannerProps {
  className?: string;
  /** Optional override for the count shown in the title (useful for previews). */
  countOverride?: number;
}

/**
 * Inline promotional banner shown at the bottom of the Evidence chat once a user has
 * submitted a threshold of Evidence queries during their Clinician trial. Clicking the
 * CTA opens the Evidence power-user upgrade modal; the dismiss X persists in localStorage.
 *
 * Design: https://www.figma.com/design/MSqFiRMYvicVZlaiWjmFG3/Trials---upgrades?node-id=9560-43279
 */
export const EvidencePowerUserBanner = ({
  className,
  countOverride,
}: EvidencePowerUserBannerProps) => {
  const intl = useIntl();
  const { data: liveCount = 0 } = useUserActivityActionCount(
    UserActivityAction.evidence_chat_query
  );
  const [dismissed, setDismissed] = useAtom(evidencePowerUserBannerDismissedAtom);
  const devForceShowBanner = useAtomValue(evidencePowerUserDevForceShowBannerAtom);
  const setDevForceShowBanner = useSetAtom(evidencePowerUserDevForceShowBannerAtom);
  const setModalOpen = useSetAtom(evidencePowerUserUpgradeModalOpenAtom);
  const { baseProps } = useEvidenceAnalytics();
  const hasTrackedImpressionRef = useRef(false);

  const effectiveCount = countOverride ?? liveCount;

  const isEnabled = useIsEvidencePowerUserEnabled();
  const meetsThreshold = effectiveCount >= EVIDENCE_POWER_USER_BANNER_COUNT_THRESHOLD;

  const { isClinicianTrialUser, isClinicianPracticeTrialUser } = useReverseTrial();

  const isNonProduction = getEnvironment() !== ENV_NAME.PROD;
  const forceShow = isNonProduction && devForceShowBanner;

  const visible =
    forceShow ||
    (isEnabled &&
      !dismissed &&
      meetsThreshold &&
      (isClinicianTrialUser || isClinicianPracticeTrialUser));

  useEffect(() => {
    if (visible && !hasTrackedImpressionRef.current) {
      hasTrackedImpressionRef.current = true;
      track({
        event_name: TrackingEventName.ButtonClicked,
        props: {
          ...baseProps,
          label: 'evidence_power_user_banner_viewed',
          feature: 'evidence',
        },
      });
    }
  }, [visible, baseProps]);

  const handleUpgradeClick = useCallback(() => {
    track({
      event_name: TrackingEventName.ButtonClicked,
      props: {
        ...baseProps,
        action: ButtonAction.EvidenceProTrialClicked,
        label: 'evidence_power_user_banner_keep_evidence_clicked',
        feature: 'evidence',
      },
    });
    setModalOpen(true);
  }, [baseProps, setModalOpen]);

  const handleDismiss = useCallback(() => {
    track({
      event_name: TrackingEventName.ButtonClicked,
      props: {
        ...baseProps,
        label: 'evidence_power_user_banner_dismissed',
        feature: 'evidence',
      },
    });
    setDevForceShowBanner(false);
    setDismissed(true);
  }, [baseProps, setDevForceShowBanner, setDismissed]);

  return (
    <AnimatePresence>
      {visible && (
        <m.div
          initial={{ opacity: 0, y: 8, height: 0 }}
          animate={{ opacity: 1, y: 0, height: 'auto' }}
          exit={{ opacity: 0, y: 8, height: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="overflow-hidden"
          data-testid="evidence-power-user-banner"
        >
          <Banner subVariant="pro" className={cn('my-3', className)}>
            <BannerContent icon={<Rocket />}>
              <BannerTitle data-testid="evidence-power-user-banner-title">
                <FormattedMessage
                  defaultMessage="You've used Evidence {count, plural, one {# time} other {# times}}"
                  id="7if3kcwldC"
                  description="Title of the Evidence power-user promo banner shown at the bottom of the Evidence chat, with a personalised usage count"
                  values={{ count: effectiveCount }}
                />
              </BannerTitle>
              <BannerDescription>
                <FormattedMessage
                  defaultMessage="Keep it in your workflow after your trial."
                  id="tZbnTW2yAS"
                  description="Subtitle of the Evidence power-user promo banner, encouraging the user to upgrade"
                />
              </BannerDescription>
            </BannerContent>
            <BannerAction>
              <Button
                type="button"
                variant="pro"
                size="xs"
                onClick={handleUpgradeClick}
                data-testid="evidence-power-user-banner-cta"
                aria-label={intl.formatMessage({
                  defaultMessage: 'Keep Evidence — open upgrade modal',
                  id: 'FUx/MgLZ3Q',
                  description:
                    'Aria label for the Keep Evidence CTA on the Evidence power-user promo banner',
                })}
              >
                <TierProFilled aria-hidden />
                <FormattedMessage
                  defaultMessage="Keep Evidence"
                  id="8+ruebdJYm"
                  description="CTA button on the Evidence power-user promo banner that opens the upgrade modal"
                />
              </Button>
            </BannerAction>
            <BannerClose
              onClick={handleDismiss}
              data-testid="evidence-power-user-banner-close"
            />
          </Banner>
        </m.div>
      )}
    </AnimatePresence>
  );
};
